import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import API from 'services/API';
import { Endpoints } from 'UI/constants/endpoints';
import { RequestStatus } from 'UI/constants/status';

const INITIAL_CATALOG_COLORS = {
  candidateTypeColors: {},
  candidateStatusColors: {}
};

const mapCandidateTypeColors = candidateTypes => {
  if (!Array.isArray(candidateTypes)) return {};

  return candidateTypes.reduce((acc, candidateType) => {
    if (candidateType?.id === null || candidateType?.id === undefined) {
      return acc;
    }

    return {
      ...acc,
      [candidateType.id]: candidateType.style_class_name || candidateType.color
    };
  }, {});
};

const mapCandidateStatusColors = candidateStatuses => {
  if (!Array.isArray(candidateStatuses)) return {};

  return candidateStatuses.reduce((acc, candidateStatus) => {
    if (candidateStatus?.id === null || candidateStatus?.id === undefined) {
      return acc;
    }

    return {
      ...acc,
      [candidateStatus.id]: candidateStatus.style || candidateStatus.color
    };
  }, {});
};

export const useResumeSearchCatalogs = () => {
  const catalogsRequestRef = useRef(null);
  const cachedCatalogColorsRef = useRef(null);
  const [uiState, setUiState] = useState(RequestStatus.Idle);
  const [catalogColors, setCatalogColors] = useState(INITIAL_CATALOG_COLORS);

  const getResumeSearchCatalogColors = useCallback(async signal => {
    if (cachedCatalogColorsRef.current) {
      return cachedCatalogColorsRef.current;
    }

    if (!catalogsRequestRef.current) {
      catalogsRequestRef.current = Promise.all([
        API.get(Endpoints.CandidateTypes, { signal }),
        API.get(Endpoints.CandidateStatuses, { signal })
      ])
        .then(([candidateTypesResponse, candidateStatusesResponse]) => {
          const mappedColors = {
            candidateTypeColors: mapCandidateTypeColors(candidateTypesResponse.data),
            candidateStatusColors: mapCandidateStatusColors(candidateStatusesResponse.data)
          };

          cachedCatalogColorsRef.current = mappedColors;
          return mappedColors;
        })
        .catch(error => {
          catalogsRequestRef.current = null;
          throw error;
        });
    }

    return catalogsRequestRef.current;
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchCatalogs = async () => {
      if (cachedCatalogColorsRef.current) {
        setCatalogColors(cachedCatalogColorsRef.current);
        setUiState(RequestStatus.Success);
        return;
      }

      setUiState(RequestStatus.Loading);

      try {
        const mappedColors = await getResumeSearchCatalogColors(controller.signal);

        setCatalogColors(mappedColors);
        setUiState(RequestStatus.Success);
      } catch (error) {
        if (axios.isCancel(error)) return;

        setUiState(RequestStatus.Error);
      }
    };

    fetchCatalogs();

    return () => controller.abort();
  }, [getResumeSearchCatalogColors]);

  return {
    ...catalogColors,
    uiState
  };
};
